import {
  Activity,
  Building2,
  Cloud,
  FileText,
  FolderTree,
  KeyRound,
  LayoutDashboard,
  LifeBuoy,
  Network,
  Radar,
  Settings,
  ShieldAlert,
  Sparkles,
  Users,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

/**
 * Grouped sidebar IA per REWRITE-PLAN §4. The command palette reuses this
 * config for its "Pages" group, so labels/paths live in exactly one place.
 */

/**
 * Live badge a nav item may carry. Severity-tiered (sidebar.tsx):
 *  - `pending-sars`: routine workload (neutral tone) — pending access requests.
 *  - `violations`:   open compliance violations (sev-critical) — risk.
 *  - `env-errors`:   environments in `status='error'` (sev-critical) — "are we
 *                    blind anywhere", the signal a CISO needs in every view.
 */
export type NavBadge = 'pending-sars' | 'violations' | 'env-errors'

/** Sub-tab under a nav item — same route, different search params. */
export interface NavSubItem {
  key: string
  label: string
  /** Search params the sub-link navigates with (e.g. { view: 'timeline' }). */
  search: Record<string, string>
  /** Also active when its param is absent (URLs omit default values). */
  isDefault?: boolean
}

export interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  /** Extra path prefixes that should also mark this item active. */
  also?: readonly string[]
  /** Live badges this item carries (rendered in declared order). */
  badges?: readonly NavBadge[]
  /** Sub-tabs rendered under the item while it is active (expanded rail only). */
  children?: readonly NavSubItem[]
}

export interface NavSection {
  label: string
  items: readonly NavItem[]
}

export const NAV_SECTIONS: readonly NavSection[] = [
  {
    label: 'Overview',
    items: [{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard }],
  },
  {
    label: 'Posture',
    items: [
      { to: '/findings', label: 'Findings', icon: ShieldAlert },
      { to: '/analyze', label: 'Analyze', icon: Sparkles },
      { to: '/iam-analytics', label: 'IAM Analytics', icon: Network },
      { to: '/identities', label: 'Identities', icon: Users },
      { to: '/file-sharing', label: 'File Sharing', icon: FolderTree },
    ],
  },
  {
    label: 'Activity',
    items: [
      {
        to: '/access-trail',
        label: 'Access Trail',
        icon: Activity,
        also: ['/audit-trail'],
        // Mirrors VIEWS in pages/access-trail/url-state.ts; 'timeline' is the
        // page default (DEFAULT_STATE.view), so its URL omits the param (isDefault).
        children: [
          { key: 'timeline', label: 'Timeline', search: { view: 'timeline' }, isDefault: true },
          { key: 'heatmap', label: 'Heatmap', search: { view: 'heatmap' } },
          { key: 'events', label: 'Events', search: { view: 'events' } },
          { key: 'apps', label: 'Apps', search: { view: 'apps' } },
          { key: 'dormant', label: 'Dormant', search: { view: 'dormant' } },
          { key: 'bursts', label: 'Bursts', search: { view: 'bursts' } },
        ],
      },
      { to: '/snapshots', label: 'Snapshots', icon: Radar, also: ['/snapshotruns'], badges: ['env-errors'] },
    ],
  },
  {
    label: 'Governance',
    items: [
      {
        to: '/access-requests',
        label: 'Access Requests',
        icon: KeyRound,
        badges: ['violations', 'pending-sars'],
      },
      { to: '/reports', label: 'Reports', icon: FileText },
    ],
  },
  {
    label: 'Admin',
    items: [
      { to: '/envs', label: 'Environments', icon: Cloud, badges: ['env-errors'] },
      { to: '/organization', label: 'Organization', icon: Building2 },
      { to: '/settings', label: 'Settings', icon: Settings },
      { to: '/feedback', label: 'Feedback & Support', icon: LifeBuoy },
    ],
  },
]

export const ALL_NAV_ITEMS: readonly NavItem[] = NAV_SECTIONS.flatMap((s) => s.items)

export function isNavActive(item: NavItem, pathname: string): boolean {
  const prefixes = [item.to, ...(item.also ?? [])]
  return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

export function navItemForPath(pathname: string): NavItem | undefined {
  return ALL_NAV_ITEMS.find((item) => isNavActive(item, pathname))
}
